import { Device, i18n } from '@goodboydigital/astro';
import { Container, Sprite } from 'pixi.js';

import { App, Shared } from '../App';
import { Settings } from '../AppConfig';
import { DecoEntity } from '../game/entities/DecoEntity';
import { AnalyticsEvent, AnalyticsState } from '../plugins/AnalyticsPlugin';
import { NineSliceButton } from '../ui/buttons/NineSliceButton';
import LocalisedText from '../ui/LocalisedText';
import { PurchasePanel } from '../ui/PurchasePanel';
import { SwipeScroller } from '../ui/SwipeScroller';
import { ShopPanel } from './../ui/ShopPanel';
import { RideStudioBaseShopScreen, RideStudioBaseShopScreenOptions } from './RideStudioBaseShopScreen';

export class RideStudioExtrasShopScreen extends RideStudioBaseShopScreen
{
    protected options: RideStudioBaseShopScreenOptions;
    protected selectedText!: LocalisedText;
    protected tickIcon!: Sprite;

    private _currentPartID: string;
    private _startPartID: string;
    private _selectContainer!: Sprite;
    private _leftSwipeScroller!: SwipeScroller;

    constructor(app: App, options: RideStudioBaseShopScreenOptions)
    {
        super(app, options);

        this.type = 'extras';
        this._currentPartID = '';
        this._startPartID = '';
        this.options = options;
    }

    public show(): void
    {
        super.show();
        
        this.app.analytics.trackState(AnalyticsState.EXTRA);
        
        this.selectedText.visible = !Settings.spineDebug;
        this._selectContainer.visible = !Settings.spineDebug;
        this.updateExtrasIcon();

        this.rightPanel.titleText.setText('rideStudioExtrasShopScreen_type');

        this._currentPartID = Shared.game.garage.selectedExtra;
        this._startPartID = this._currentPartID;
        this.displayItemUnlockedStatus();
        this._leftSwipeScroller.refreshContent();
    }

    public hide(): void
    {
        
    }

    protected buildRightPanel(): void
    {
        this.rightPanel = new ShopPanel({
            title: '',
            w: this.panelWidth,
            h: this.panelHeight,
            radioButtonOptions: [],
            omitArrows: Device.mobile,
        });
        this.view.addChild(this.rightPanel.view);

        this._leftSwipeScroller = this.createSwipeScroller();
        this.rightPanel.content.addChild(this._leftSwipeScroller.view);

        // connect buttons
        this.rightPanel.signals.onArrowUp.connect(() =>
        {
            this._leftSwipeScroller.nextItem();
            //this.displayItemUnlockedStatus();
            
            this.selectedText.visible = false;
            this.tickIcon.visible = false;
        });
        this.rightPanel.signals.onArrowDown.connect(() =>
        {
            this._leftSwipeScroller.prevItem();
            //this.displayItemUnlockedStatus();
            
            this.selectedText.visible = false;
            this.tickIcon.visible = false;
        });

        this.selectedText = new LocalisedText('rideStudioExtrasShopScreen_selectedText', {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            stroke: 0x333333,
            strokeThickness: 6,
            lineJoin: 'round',
            align: 'center',
            fontSize: 26,
            maxWidth: 250,
        });

        this.selectedText.anchor.set(0.5);
        this.selectedText.y = 65;
        this.rightPanel.view.addChild(this.selectedText);
        
        this.tickIcon = Sprite.from(Shared.assets.getTexture('equipped-tick'));
        this.tickIcon.scale.set(1.25);
        this.tickIcon.anchor.set(0.5);
        this.tickIcon.position.set(-60, 30);
        this.rightPanel.view.addChild(this.tickIcon);

        this._selectContainer = new Sprite();
        this._selectContainer.anchor.set(0.5);
        this._selectContainer.y = 280;
        this.rightPanel.addChild(this._selectContainer);
        this.displayItemUnlockedStatus();
    }

    protected displayItemUnlockedStatus(): void
    {
        if (Settings.spineDebug) return;

        this._selectContainer.removeChildren();
        const unlocked = Shared.game.shop.checkPurchased(Shared.game.garage.selectedExtra, 'deco');
        const data = this.app.data.getDecoData(Shared.game.garage.selectedExtra);

        this.selectedText.visible = false;
        this.tickIcon.visible = false;

        if (unlocked)
        {
            this._currentPartID = Shared.game.garage.selectedExtra;
            this.selectedText.visible = true;
            this.tickIcon.visible = true;
            
            this.confirmButton.enable();
            
        }
        else
        {
            
            this.confirmButton.disable();
            
            const shopSystem = Shared.game.shop;
            const purchase = new PurchasePanel({
                enableCoinPurchase: true,
                enableDiamondPurchase: true,
                enableCancel: false,
                unlockCoinCost: data.coinPrice ?? 500,
                unlockDiamondCost: data.diamondPrice ?? 50,
            });

            this.selectedText.visible = false;
            this.tickIcon.visible = false;

            purchase.signals.coinBtnClick.connect((cost) =>
            {
                if (shopSystem.checkCoinsAvailable(cost))
                {
                    this.app.popup.presentConfirmPurchasePopup({
                        title: 'purchasePopup_title',
                        message: 'purchasePopup_message_generic',
                        confirm: 'purchasePopup_confirm',
                        cancel: 'purchasePopup_cancel',
                        plainSprite: DecoEntity.mountIcon(Shared.game.garage.selectedExtra),
                        coinCost: cost,
                        onConfirm: () => this._purchaseItem(cost),
                    });
                }
            });

            purchase.signals.diamondBtnClick.connect((cost) =>
            {
                if (shopSystem.checkDiamondsAvailable(cost))
                {
                    this.app.popup.presentConfirmPurchasePopup({
                        title: 'purchasePopup_title',
                        message: 'purchasePopup_message_generic',
                        confirm: 'purchasePopup_confirm',
                        cancel: 'purchasePopup_cancel',
                        plainSprite: DecoEntity.mountIcon(Shared.game.garage.selectedExtra),
                        diamondCost: cost,
                        onConfirm: () => this._purchaseItem(0, cost),
                    });
                }
            });

            this._selectContainer.addChild(purchase.view);
        }
    }

    protected updateExtrasIcon(): void
    {
        const garage = Shared.game.garage;

        this._leftSwipeScroller.setCurrentIndex(garage.extras.indexOf(garage.selectedExtra));
    }

    protected buildLeftPanel(): void
    {
        this.leftPanel = new ShopPanel({
            title: '',
            w: this.panelWidth,
            h: this.panelHeight,
            radioButtonOptions: [],
        });
    }
    
    private _purchaseItem(coins?: number, diamonds?: number): void
    {
        const shopSystem = Shared.game.shop;
        shopSystem.purchaseItem(Shared.game.garage.selectedExtra, 'deco', coins, diamonds);
        this._currentPartID = Shared.game.garage.selectedExtra;
        Shared.game.garage.saveVehicleSelection();
        this.displayItemUnlockedStatus();
        this._selectContainer.removeChildren();
        this._leftSwipeScroller.refreshContent();
        this.app.analytics.trackEvent(AnalyticsEvent.CONTENT_UNLOCKED, { game_item: `extras|${Shared.game.garage.selectedExtra}`});
        
        if(shopSystem.checkAllTypeUnlocked("deco")){
            this.app.analytics.trackEvent(AnalyticsEvent.PURCHASED_ALL_ITEMS, { game_item: `extras|${Shared.game.garage.selectedExtra}`, content_type: 'extras'});
        }
    }

    // ==== SWIPE SCROLLER ========================================================================

    private createSwipeScroller(): SwipeScroller
    {
        const scroller = new SwipeScroller({
            length: Shared.game.garage.extras.length,
            offsetY: -10,
            width: 274,
            height: 454,
            spacing: 150,
        }, this.leftSwipeScrollerContentProvider.bind(this));

        scroller.onSnap.connect(this.swipeScrollerSnapHandler.bind(this));
        scroller.onStartDrag.connect(this.swipeScrollerStartDragHandler.bind(this));

        return scroller;
    }

    /**
     * Provide sprites to a swipe scroller
     * @param index - Reference index for the content
     * @returns Sprite to be used inside the scroller
     */
    private leftSwipeScrollerContentProvider(index: number): Container
    {
        const garage = Shared.game.garage;

        const icon = DecoEntity.mountIcon(garage.extras[index]);

        icon.scale.set(Math.min(140 / icon.width, 2.2));

        const unlocked = Shared.game.shop.checkPurchased(garage.extras[index], 'deco');
        const selected = garage.extras[index] === this._currentPartID;

        if (!selected && !unlocked)
        {
            const lock = Sprite.from(Shared.assets.getTexture('icon-locked'));

            lock.scale.set(0.75 / icon.scale.x);
            lock.anchor.set(0.5);
            lock.position.set(-30 / icon.scale.x, 30 / icon.scale.x);

            icon.addChild(lock);
        }

        return icon;
    }

    /** Fires when scroller snaps to one of its items */
    private swipeScrollerSnapHandler(scroller: SwipeScroller): void
    {
        const index = scroller.getCurrentIndex();

        Shared.game.garage.setExtra(Shared.game.garage.extras[index]);
        this.displayItemUnlockedStatus();
    }

    /** Fires when user start dragging the scroller */
    private swipeScrollerStartDragHandler(): void
    {
        this.selectedText.visible = false;
        this.tickIcon.visible = false;
        this._selectContainer.removeChildren();
    }
    
    protected onCancelButtonPressed(): void {
        super.onCancelButtonPressed();
        
        Shared.game.garage.setExtra(this._startPartID);
    }
    
    protected onConfirmButtonPressed(): void {
        super.onConfirmButtonPressed();
        
        //this.app.analytics.trackEvent(AnalyticsEvent.CUSTOMISEPAGE_ITEMSELECTION_CLICK, { game_item: `garage|extras|${this._currentPartID}` });
        Shared.game.garage.setExtra(this._currentPartID);
        
        Shared.game.garage.saveVehicleSelection();
    }
}
